// @flow

import type { Endpoints, EntityFieldCollection, SortParams } from '../types';
import Admin from './Admin';
import { createdAtField, getCreatedAtSort, idField } from '../abstract/StoredEntity';
import type { StoredEntity } from '../abstract/StoredEntity';
import { constructEndpoints } from '../helpers';

class ContactRequest implements StoredEntity {
    static classInternalName = 'contact_request';

    static statusOptions = {
        new: 'New',
        acknowledged: 'Acknowledged',
        assigned: 'Assigned',
        contacted: 'Contacted',
        nurturing: 'Nurturing',
        won: 'Won',
        lost: 'Lost',
        closed: 'Closed',
    };

    static fields: EntityFieldCollection = {
        id: idField,
        createdAt: createdAtField,
        name: {
            displayName: 'Name',
            recordName: 'name',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: false,
            isOptional: false,
            displayOnMain: true,
        },
        email: {
            displayName: 'Email',
            recordName: 'email',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: false,
            isOptional: false,
            displayOnMain: true,
        },
        status: {
            displayName: 'Status',
            recordName: 'status',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: true,
            isOptional: false,
            displayOnMain: true,
        },
        acknowledgedAt: {
            displayName: 'Acknowledged At',
            recordName: 'acknowledged_at',
            dataType: {
                primitive: 'ts',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: false,
            isOptional: true,
            displayOnMain: false,
        },
        locale: {
            displayName: 'Locale',
            recordName: 'locale',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: false,
            isOptional: false,
            displayOnMain: true,
        },
        assigneeName: {
            displayName: 'Assignee Name',
            recordName: 'assignee_name',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: true,
            isOptional: true,
            displayOnMain: true,
        },
        assigneeEmail: {
            displayName: 'Assignee Email',
            recordName: 'assignee_email',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: true,
            isOptional: true,
            displayOnMain: false,
        },
        assignedAdminId: {
            displayName: 'Assigned Admin',
            recordName: 'assigned_admin_id',
            dataType: {
                primitive: 'ref',
                isArray: false,
                extraData: {
                    maxValues: 1,
                    model: () => Admin,
                },
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: false,
            canEdit: true,
            isOptional: true,
            displayOnMain: true,
        },
        lastActivityAt: {
            displayName: 'Last Activity At',
            recordName: 'last_activity_at',
            dataType: {
                primitive: 'ts',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: false,
            isOptional: true,
            displayOnMain: true,
        },
        lastActivityBy: {
            displayName: 'Last Activity By',
            recordName: 'last_activity_by',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: false,
            isOptional: true,
            displayOnMain: false,
        },
        lastContactedAt: {
            displayName: 'Last Contacted At',
            recordName: 'last_contacted_at',
            dataType: {
                primitive: 'ts',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: true,
            isOptional: true,
            displayOnMain: false,
        },
        nextFollowUpAt: {
            displayName: 'Next Follow Up',
            recordName: 'next_follow_up_at',
            dataType: {
                primitive: 'ts',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: true,
            isOptional: true,
            displayOnMain: true,
        },
        notes: {
            displayName: 'Notes',
            recordName: 'notes',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: false,
            canEdit: true,
            isOptional: true,
            displayOnMain: false,
        },
        notifiedAt: {
            displayName: 'Notified At',
            recordName: 'notified_at',
            dataType: {
                primitive: 'ts',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: false,
            isOptional: true,
            displayOnMain: true,
        },
        reminder24hSentAt: {
            displayName: '24h Reminder Sent',
            recordName: 'reminder_24h_sent_at',
            dataType: {
                primitive: 'ts',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: false,
            isOptional: true,
            displayOnMain: false,
        },
        assigneeReminderSentAt: {
            displayName: 'Assignee Reminder Sent',
            recordName: 'assignee_reminder_sent_at',
            dataType: {
                primitive: 'ts',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: true,
            needToCreate: false,
            canEdit: false,
            isOptional: true,
            displayOnMain: false,
        },
        source: {
            displayName: 'Source',
            recordName: 'source',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: false,
            canEdit: false,
            isOptional: false,
            displayOnMain: false,
        },
        message: {
            displayName: 'Message',
            recordName: 'message',
            dataType: {
                primitive: 'str',
                isArray: false,
                extraData: null,
            },
            supportsQuery: true,
            supportsSort: false,
            needToCreate: false,
            canEdit: false,
            isOptional: false,
            displayOnMain: false,
        },
    };

    static fileFields: EntityFieldCollection = {};

    static defaultSort: SortParams = getCreatedAtSort('created_at');

    get id() {
        return this.record[ContactRequest.fields.id.recordName];
    }

    get displayName() {
        return `${this.record[ContactRequest.fields.name.recordName]} <${this.record[ContactRequest.fields.email.recordName]}>`;
    }

    get statusLabel() {
        return ContactRequest.statusOptions[this.record[ContactRequest.fields.status.recordName]] || this.record[ContactRequest.fields.status.recordName];
    }

    static endpoints: Endpoints = constructEndpoints('contact-requests');

    constructor(record: any) {
        this.record = record;
    }
}

export default ContactRequest;